import { ISelectItem } from "@components/inputs/select/Select.types";
import { IFieldInfoFilter, IFieldInfoProperties, IGetValueArgs } from "@components/smart/FieldInfo";
import { addNewLineItem } from "@components/smart/smartFastEntryList";
import { expandEnumProps, getExpandedEnumProps } from "@odata/Data.utils";
import {
    CsszEmploymentTypeEntity,
    CzIsceEntity,
    CzIscoEntity,
    DetailedEmploymentSpecificationEntity,
    EntitySetName,
    EntityTypeName,
    ICsszEmploymentTypeEntity,
    IPrAttendanceEntity,
    IPrBenefitEntity,
    IPrEmploymentBenefitEntity,
    IPrEmploymentEntity,
    IPrEmploymentExtraSalaryEntity,
    IPrEmploymentSalaryComponentEntity,
    IPrEmploymentTemplateBenefitEntity,
    IPrEmploymentTemplateEntity,
    IPrEmploymentTemplateExtraSalaryEntity,
    IPrEmploymentTemplateSalaryComponentEntity,
    IPrExtraSalaryEntity,
    IPrExtraSalaryLegislativeValueEntity,
    IPrExtraSalaryTypeEntity,
    IPrPayrollSettingEntity,
    IPrPayslipEmploymentExtraSalaryEntity,
    IPrSalaryComponentEntity,
    PrAttendanceEntity,
    PrBenefitEntity,
    PrEmploymentBenefitEntity,
    PrEmploymentEntity,
    PrEmploymentSalaryComponentEntity,
    PrEmploymentTemplateBenefitEntity,
    PrEmploymentTemplateEntity,
    PrEmploymentTemplateSalaryComponentEntity,
    PrEmploymentTemplateTemporalCurrentEntity,
    PrEmploymentTypeEntity,
    PrExtraSalaryEntity,
    PrExtraSalaryTypeEntity,
    PrPayrollSettingEntity,
    PrPayrollSettingTemporalCurrentEntity,
    PrSalaryComponentEntity,
    ShiftPatternEntity
} from "@odata/GeneratedEntityTypes";
import {
    CzIsceCode,
    PrAttendanceStatusCode,
    PrEmploymentTypeCode,
    PrEntityValueSourceCode,
    PrExtraSalaryOriginCode,
    ShiftPatternCode
} from "@odata/GeneratedEnums";
import { getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { BatchRequest, isBatchResultOk, OData } from "@odata/OData";
import { transformToODataString } from "@odata/OData.utils";
import { ODataQueryResult } from "@odata/ODataParser";
import {
    createNewTemporalLineItem,
    getTemporalDataColumns,
    groupAndOptimizeRanges,
    replaceSyncedValueWithHistoryValues,
    TTemporal
} from "@odata/TemporalUtils";
import {
    BenefitVariant,
    getBenefitColumns,
    getBenefitTemporalDialogSettings
} from "@pages/payroll/benefits/Benefit.utils";
import { getEmployeeSalaryCurrencyPath } from "@pages/payroll/employee/Employee.utils";
import { IEmploymentCustomData } from "@pages/payroll/employment/EmploymentFormView";
import { createDateFromPeriod } from "@pages/payroll/payroll.utils";
import {
    getSalaryComponentColumns,
    getSalaryComponentTemporalDialogSettings,
    getSalaryEnumNamespaceNames,
    SalaryComponentVariant
} from "@pages/payroll/salaryComponent/SalaryComponent.utils";
import { getProperty, isObjectEmpty, setProperty } from "@utils/general";
import memoize from "@utils/memoize";

import { BasicInputSizes, FieldType, ValueType } from "../../../enums";
import BindingContext, { createBindingContext, createPath, IEntity } from "../../../odata/BindingContext";
import { DATE_MAX, getUtcDayjs } from "../../../types/Date";
import { FormStorage } from "../../../views/formView/FormStorage";
import {
    createLegislativeExtraSalaries,
    ExtraSalaryVariant,
    getExtraSalaryColumns,
    getExtraSalaryTemporalDialogSettings
} from "../extraSalary/ExtraSalary.utils";

export enum DialogType {
    SalaryComponent = "SalaryComponent",
    ExtraSalary = "ExtraSalary",
    Benefit = "Benefit"
}

export const EmploymentTranslationFiles = [
    "Employment", "Employee", "NumberRange", "Common",
    "SalaryComponent", "ExtraSalary", "Benefit",
    "RetirementSavingsCompany",
    "Submission",
    ...getSalaryEnumNamespaceNames(),
    getEnumNameSpaceName(EntityTypeName.PrBenefitType)
];

export const LegislativeExtraSalaryLocalPath = BindingContext.localContext("LegislativeExtraSalaries");
export const ExtraSalaryTogglePath = BindingContext.localContext("LegislativeExtraSalariesToggle");

export const ExtraSalaryGrpId = PrEmploymentTemplateEntity.ExtraSalaries;
export const SalaryComponentGrpId = PrEmploymentTemplateEntity.SalaryComponents;
export const BenefitGrpId = PrEmploymentTemplateEntity.Benefits;

export enum EmploymentStatus {
    Active = "Active",
    Planned = "Planned",
    Finished = "Finished",
}

const getCurrentValidityFilter = (E: typeof CzIsceEntity | typeof CzIscoEntity): IFieldInfoFilter => {
    return {
        select: () => {
            // keep as callback, so the filter is actual at the time of execution, not at the time of def generation.
            const now = getUtcDayjs().toDate();
            return [
                `(${E.DateValidFrom} le ${transformToODataString(now, ValueType.Date)} OR ${E.DateValidFrom} eq null)`,
                `(${E.DateValidTo} ge ${transformToODataString(now, ValueType.Date)} OR ${E.DateValidTo} eq null)`,
            ].join(" AND ");
        },
    };
};

export const getIscoDef: () => IFieldInfoProperties = () => ({
    type: FieldType.ComboBox,
    fieldSettings: {
        displayName: CzIscoEntity.MpsvCode,
        shouldDisplayAdditionalColumns: true,
        orderBy: {
            propertyName: CzIscoEntity.MpsvCode,
            asc: true
        },
        preloadItems: true // todo: solve columns when creating initialItems
    },
    filter: getCurrentValidityFilter(CzIscoEntity),
    columns: [{
        id: CzIscoEntity.MpsvCode
    }, {
        id: CzIscoEntity.Description,
    }]
});

export const getIsceDef: () => IFieldInfoProperties = () => ({
    type: FieldType.ComboBox,
    fieldSettings: {
        displayName: CzIsceEntity.MpsvCode,
        shouldDisplayAdditionalColumns: true,
        orderBy: {
            propertyName: CzIsceEntity.MpsvCode,
            asc: true
        },
        preloadItems: true // todo: solve columns when creating initialItems
    },
    filter: getCurrentValidityFilter(CzIsceEntity),
    columns: [{
        id: CzIsceEntity.MpsvCode
    }, {
        id: CzIsceEntity.Description,
    }]
});

export function predefineIsceProperty(storage: FormStorage, employmentType: PrEmploymentTypeCode, dateEnd: Date, pathToIsce: string): void {
    let isceCode: CzIsceCode = null;
    const hasDateEnd = dateEnd && getUtcDayjs(dateEnd).isBefore(DATE_MAX, "year");
    switch(employmentType) {
        case PrEmploymentTypeCode.Compensation:
            isceCode = CzIsceCode.CZISCE1121;
            break;
        case PrEmploymentTypeCode.Employment:
            isceCode = hasDateEnd ? CzIsceCode.CZISCE1112 : CzIsceCode.CZISCE1111;
            break;
        case PrEmploymentTypeCode.WorkAgreementDPC:
            isceCode = hasDateEnd ? CzIsceCode.CZISCE1212 : CzIsceCode.CZISCE1211;
            break;
        case PrEmploymentTypeCode.WorkAgreementDPP:
            isceCode = hasDateEnd ? CzIsceCode.CZISCE1222: CzIsceCode.CZISCE1221;
            break;
    }
    storage.setValueByPath(pathToIsce, isceCode);
}

export const shiftPatternDef = {
    type: FieldType.ComboBox,
    width: BasicInputSizes.L,
    defaultValue: ShiftPatternCode.NA,
    fieldSettings: {
        displayName: ShiftPatternEntity.Name,
        orderBy: {
            propertyName: ShiftPatternEntity.MpsvCode,
            asc: true
        }
    }
};

export const detailedSpecificationDef = {
    type: FieldType.ComboBox,
    fieldSettings: {
        displayName: DetailedEmploymentSpecificationEntity.Name,
        orderBy: {
            propertyName: DetailedEmploymentSpecificationEntity.MpsvCode,
            asc: true
        },
    }
};

export function getFilterForTabId(tabId: string): string {
    const nowDate = getUtcDayjs().startOf("day").toISOString().substring(0, 10);
    switch (tabId as EmploymentStatus) {
        case EmploymentStatus.Active:
            return `${PrEmploymentEntity.DateStart} le ${nowDate} and (${PrEmploymentEntity.DateEnd} ge ${nowDate} or ${PrEmploymentEntity.DateEnd} eq null)`;
        case EmploymentStatus.Planned:
            return `${PrEmploymentEntity.DateStart} gt ${nowDate}`;
        case EmploymentStatus.Finished:
            return `${PrEmploymentEntity.DateEnd} lt ${nowDate}`;
    }
    return "";
}

export function getEmploymentStatus(entity: IPrEmploymentEntity): EmploymentStatus {
    const now = getUtcDayjs();
    if (now.isSameOrAfter(entity.DateStart) && (!entity.DateEnd || now.isSameOrBefore(entity.DateEnd))) {
        return EmploymentStatus.Active;
    } else if (now.isBefore(entity.DateStart)) {
        return EmploymentStatus.Planned;
    } else {
        return EmploymentStatus.Finished;
    }
}

export function getEmploymentCurrencyPath(): string {
    return getEmployeeSalaryCurrencyPath(PrEmploymentEntity.Employee);
}

function isPayslipExtraSalary(item: unknown): item is IPrPayslipEmploymentExtraSalaryEntity {
    return !!(item as IPrPayslipEmploymentExtraSalaryEntity)?.Type;
}

export function isLegislativeExtraSalaryItem(salaryItem: IPrPayslipEmploymentExtraSalaryEntity | IPrEmploymentExtraSalaryEntity): boolean {
    let type: IPrExtraSalaryTypeEntity;
    if (isPayslipExtraSalary(salaryItem)) {
        type = salaryItem.Type;
    } else {
        type = salaryItem?.Default?.Type;
    }
    return type?.OriginCode === PrExtraSalaryOriginCode.Legislative;
}

export function isLegislativeExtraSalary({ storage, bindingContext, item }: IGetValueArgs): boolean {
    if (!item) {
        item = storage.getValue(bindingContext);
    }
    // universal check for both PrEmploymentTemplateSalaryItem and PrEmploymentSalaryItem
    const employmentItem = (item as unknown as IPrEmploymentExtraSalaryEntity);
    return isLegislativeExtraSalaryItem(employmentItem);
}

export type TEmploymentEntity = IPrEmploymentEntity | IPrEmploymentTemplateEntity;
export type TEmploymentLineItemProp = PrEmploymentEntity.ExtraSalaries | PrEmploymentEntity.SalaryComponents | PrEmploymentEntity.Benefits;
export type TEmploymentTemplateLineItemProp =
    PrEmploymentTemplateEntity.ExtraSalaries
    | PrEmploymentTemplateEntity.SalaryComponents
    | PrEmploymentTemplateEntity.Benefits;

export type TBenefitLineItem = IPrEmploymentTemplateBenefitEntity | IPrEmploymentBenefitEntity;
export type TSalaryComponentLineItem = IPrEmploymentTemplateSalaryComponentEntity | IPrEmploymentSalaryComponentEntity;
export type TExtraSalaryLineItem = IPrEmploymentTemplateExtraSalaryEntity | IPrEmploymentExtraSalaryEntity;

export const getEmploymentCSSZSelectItems = memoize(async (storage: FormStorage): Promise<ISelectItem[]> => {
    const query = storage.oData.getEntitySetWrapper(EntitySetName.CsszEmploymentTypes).query()
        .select(CsszEmploymentTypeEntity.Code, CsszEmploymentTypeEntity.Name, CsszEmploymentTypeEntity.ShortName, CsszEmploymentTypeEntity.EmploymentTypeCode);

    const result = await query.fetchData<ICsszEmploymentTypeEntity[]>();

    return result.value.map((item) => ({
        id: item.Code,
        label: item.ShortName,
        tabularData: [item.ShortName, item.Name],
        additionalData: {
            [CsszEmploymentTypeEntity.EmploymentTypeCode]: item.EmploymentTypeCode
        }
    }));
});

export function getCsszEmploymentTypesForSelectedType(items: ISelectItem[], employmentType: PrEmploymentTypeCode): ISelectItem[] {
    if (!employmentType) {
        return items;
    }
    return items?.filter((item) =>
        item.additionalData.EmploymentTypeCode === employmentType);
}

export const getEmploymentTypeDef = (employmentTypePath: string): IFieldInfoProperties => ({
    type: FieldType.ComboBox,
    fieldSettings: {
        additionalProperties: [{ id: CsszEmploymentTypeEntity.ShortName }, { id: CsszEmploymentTypeEntity.Name }],
        itemsFactory: (args) => getEmploymentCSSZSelectItems(args.storage as FormStorage),
        itemsForRender: (items, args) => {
            const currentType = args.storage.getValueByPath(createPath(employmentTypePath, PrEmploymentTypeEntity.Code));
            return getCsszEmploymentTypesForSelectedType(items, currentType);
        },
        shouldDisplayAdditionalColumns: true
    },
    columns: [
        { id: CsszEmploymentTypeEntity.ShortName },
        { id: CsszEmploymentTypeEntity.Name }
    ]
});

/**
 * Returns new sourceCode for newly created line item
 * @param code as string, so we don't need to cast it when called
 * @param isFromTemplate
 */
function getSourceCode(code: string, isFromTemplate: boolean): PrEntityValueSourceCode {
    if (!isFromTemplate) {
        return PrEntityValueSourceCode.Default;
    }
    throw new Error("Not implemented.");
    // return getEmploymentSourceCodeFromTemplate(code as PrEntityValueSourceCode);
}

export function createNewSalaryComponentItemFromDefault(defaultSalaryComponent: IPrSalaryComponentEntity): TSalaryComponentLineItem {
    // change type so we are able to get "*SourceCode" with fallback, because it's not defined on IPrSalaryComponent
    const {
        CurrentTemporalPropertyBag,
        TypeCode,
        RegularSalaryTypeCode
    } = (defaultSalaryComponent as unknown as IPrEmploymentTemplateSalaryComponentEntity);

    const newItem: TSalaryComponentLineItem = createNewTemporalLineItem(getSalaryComponentTemporalDialogSettings(SalaryComponentVariant.Employment), {
        Default: defaultSalaryComponent?.Id ? { ...defaultSalaryComponent } : null,
        ...getExpandedEnumProps(EntityTypeName.PrSalaryComponentType, TypeCode, PrEmploymentSalaryComponentEntity.Type),
        ...getExpandedEnumProps(EntityTypeName.PrRegularSalaryType, RegularSalaryTypeCode, PrEmploymentSalaryComponentEntity.RegularSalaryType),
        CurrentTemporalPropertyBag: {
            ...CurrentTemporalPropertyBag
        }
    });
    // transfer all columns to new item
    getSalaryComponentColumns(SalaryComponentVariant.Employment).forEach(column => {
        const key = `${column}SourceCode`;
        const val = getSourceCode(getProperty(CurrentTemporalPropertyBag, key) as string, false);
        setProperty(newItem.CurrentTemporalPropertyBag, key, val);
    });

    return newItem as IPrEmploymentTemplateSalaryComponentEntity;
}

export function createNewExtraSalaryItemFromDefault(defaultExtraSalary: IPrExtraSalaryEntity, legislativeValues?: IPrExtraSalaryLegislativeValueEntity[]): TExtraSalaryLineItem {
    // change type so we are able to get "*SourceCode" with fallback, because it's not defined on IPrExtraSalary
    const {
        CurrentTemporalPropertyBag
    } = (defaultExtraSalary as unknown as IPrEmploymentTemplateExtraSalaryEntity);
    let defaultRange = null;

    if (defaultExtraSalary.Type?.OriginCode === PrExtraSalaryOriginCode.Legislative) {
        const {
            DateValidFrom,
            DateValidTo
        } = legislativeValues.find(value => value.ExtraSalaryTypeCode === defaultExtraSalary.TypeCode);
        defaultRange = { DateValidFrom, DateValidTo };
    }

    const newItem: TExtraSalaryLineItem = createNewTemporalLineItem(getExtraSalaryTemporalDialogSettings(), {
        Default: defaultExtraSalary?.Id ? { ...defaultExtraSalary } : null,
        CurrentTemporalPropertyBag: {
            ...CurrentTemporalPropertyBag,
        }
    }, defaultRange);

    // transfer all columns to new item
    getExtraSalaryColumns(ExtraSalaryVariant.Employment).forEach(column => {
        const key = `${column}SourceCode`;
        const val = getSourceCode(getProperty(CurrentTemporalPropertyBag, key) as string, false);
        setProperty(newItem.CurrentTemporalPropertyBag, key, val);
    });

    return newItem as IPrEmploymentTemplateExtraSalaryEntity;
}

export function createNewBenefitItemFromDefault(defaultBenefit: IPrBenefitEntity): TBenefitLineItem {
    // change type so we are able to get "*SourceCode" with fallback, because it's not defined on IPrSalaryComponent
    const {
        CurrentTemporalPropertyBag, Type, TypeCode
    } = (defaultBenefit as unknown as IPrEmploymentTemplateBenefitEntity);

    const newItem: TBenefitLineItem = createNewTemporalLineItem(getBenefitTemporalDialogSettings(BenefitVariant.Employment), {
        Type, TypeCode,
        Default: defaultBenefit?.Id ? { ...defaultBenefit } : null,
        CurrentTemporalPropertyBag: {
            ...CurrentTemporalPropertyBag
        },
    });

    // transfer all columns to new item
    getTemporalDataColumns(getBenefitColumns(BenefitVariant.Employment), false)
        .forEach(column => {
            const key = `${column}SourceCode`;
            const val = getSourceCode(getProperty(CurrentTemporalPropertyBag, key) as string, false);
            setProperty(newItem.CurrentTemporalPropertyBag, key, val);
        });

    return newItem as IPrEmploymentTemplateBenefitEntity;
}

export async function getEmploymentGeneralSettings(oData: OData, withHistory = false): Promise<IPrPayrollSettingEntity> {
    try {
        const query = oData.getEntitySetWrapper(EntitySetName.PrPayrollSettings).query()
            .expand(PrPayrollSettingEntity.CurrentTemporalPropertyBag, (q) => {
                q.select(
                    PrPayrollSettingTemporalCurrentEntity.WorkingHoursPerWeek,
                    PrPayrollSettingTemporalCurrentEntity.SickDays,
                    PrPayrollSettingTemporalCurrentEntity.VacationWeeks
                );
            });

        if (withHistory) {
            query.expand(PrPayrollSettingEntity.TemporalPropertyBag);
        }

        const settings = (await query.fetchData<IPrPayrollSettingEntity[]>()).value;

        return settings[0];
    } catch (e) {
        // todo: handle error
        return {};
    }
}

export async function createDefaultLegislativeExtraSalaryItems(storage: FormStorage): Promise<void> {
    // remove all legislative extra salaries in case there are any already (e.g. on re-init from draft, etc...)
    const entity = storage.data.entity;
    entity.ExtraSalaries = entity.ExtraSalaries?.filter((es: IPrEmploymentExtraSalaryEntity) => es.Default?.Type?.OriginCode !== PrExtraSalaryOriginCode.Legislative);
    // create new according to storage data (Type)
    const items = await createLegislativeExtraSalaries(storage, getExtraSalaryTemporalDialogSettings());

    items.forEach(item => {
        addNewLineItem(storage, PrEmploymentTemplateEntity.ExtraSalaries, PrEmploymentTemplateEntity.ExtraSalaries, [], item);
    });
}

export async function initWithPayrollSettings(storage: FormStorage): Promise<void> {
    const settings = await getEmploymentGeneralSettings(storage.oData);

    const { SickDays, VacationWeeks } = settings?.CurrentTemporalPropertyBag ?? {};
    let { CurrentTemporalPropertyBag } = storage.data.entity;

    if (!CurrentTemporalPropertyBag) {
        CurrentTemporalPropertyBag = {};
    }

    // initiated with settings default
    CurrentTemporalPropertyBag.SickDays = SickDays;
    CurrentTemporalPropertyBag.SickDaysSourceCode = PrEntityValueSourceCode.Default;
    CurrentTemporalPropertyBag.VacationWeeks = VacationWeeks;
    CurrentTemporalPropertyBag.VacationWeeksSourceCode = PrEntityValueSourceCode.Default;

    storage.data.entity.CurrentTemporalPropertyBag = CurrentTemporalPropertyBag;
}

export async function initiateWithTemplate(storage: FormStorage<IPrEmploymentEntity>, templateId: string): Promise<void> {
    const batch: BatchRequest = storage.oData.batch();
    batch.beginAtomicityGroup("group1");

    // legislative values
    // batch.getEntitySetWrapper(EntitySetName.PrExtraSalaryLegislativeValues).query()
    //     .select(
    //         PrExtraSalaryLegislativeValueEntity.ExtraSalaryTypeCode,
    //         PrExtraSalaryLegislativeValueEntity.DateValidFrom,
    //         PrExtraSalaryLegislativeValueEntity.DateValidTo
    //     );

    batch.getEntitySetWrapper(EntitySetName.PrEmploymentTemplates)
            .query(templateId)
            .expand(PrEmploymentTemplateEntity.TemporalPropertyBag)
            .expand(PrEmploymentTemplateEntity.CurrentTemporalPropertyBag, subQuery =>
                    subQuery.expand(PrEmploymentTemplateTemporalCurrentEntity.WorkingPattern)
            )
            .expand(PrEmploymentTemplateEntity.SalaryComponents, (subQuerySC) => {
                subQuerySC.expand(PrEmploymentTemplateSalaryComponentEntity.CurrentTemporalPropertyBag)
                        .expand(PrEmploymentTemplateSalaryComponentEntity.TemporalPropertyBag)
                        .expand(PrEmploymentTemplateSalaryComponentEntity.Default, qsc =>
                                qsc.expand(PrSalaryComponentEntity.CurrentTemporalPropertyBag)
                                        .expand(PrSalaryComponentEntity.TemporalPropertyBag));
            })
            .expand(PrEmploymentTemplateEntity.ExtraSalaries, (subQueryES) => {
                subQueryES
                        .expand(PrEmploymentTemplateSalaryComponentEntity.CurrentTemporalPropertyBag)
                        .expand(PrEmploymentTemplateSalaryComponentEntity.TemporalPropertyBag)
                        .expand(PrEmploymentTemplateSalaryComponentEntity.Default, qes3 =>
                            qes3.select(PrExtraSalaryEntity.Id, PrExtraSalaryEntity.TypeCode)
                                        .expand(PrExtraSalaryEntity.TemporalPropertyBag)
                                        .expand(PrExtraSalaryEntity.CurrentTemporalPropertyBag)
                                        .expand(PrExtraSalaryEntity.Type, qes4 =>
                                            qes4.select(PrExtraSalaryTypeEntity.OriginCode))
                        );
            })
            .expand(PrEmploymentTemplateEntity.Benefits, (subQueryA) => {
                subQueryA
                        .expand(PrEmploymentTemplateBenefitEntity.CurrentTemporalPropertyBag)
                        .expand(PrEmploymentTemplateBenefitEntity.TemporalPropertyBag)
                        .expand(PrEmploymentTemplateBenefitEntity.Default, qa3 =>
                                qa3.select(PrBenefitEntity.Id, PrBenefitEntity.TypeCode)
                                        .expand(PrBenefitEntity.CurrentTemporalPropertyBag)
                                        .expand(PrBenefitEntity.TemporalPropertyBag)
                        );
            });

    const results = await batch.execute();
    if (results.every(res => isBatchResultOk(res))) {

        // const legislativeValues = (results[0].body as ODataQueryResult).value as IPrExtraSalaryLegislativeValueEntity[];
        const template = (results[0].body as ODataQueryResult).value as IPrEmploymentTemplateEntity;
        const templateBc = createBindingContext(EntitySetName.PrEmploymentTemplates, storage.oData.getMetadata()).addKey(templateId);
        expandEnumProps(template, templateBc);

        const { CurrentTemporalPropertyBag, TemporalPropertyBag, SalaryComponents, ExtraSalaries, Benefits } = template;

        storage.data.entity = {
            ...storage.data.entity,
            SalaryComponents: [ ...SalaryComponents ],
            ExtraSalaries: [ ...ExtraSalaries ],
            Benefits: [ ...Benefits ],
            CurrentTemporalPropertyBag: { ...CurrentTemporalPropertyBag },
            TemporalPropertyBag: [ ...TemporalPropertyBag ]
        };
        storage.refresh();
    }
}

export function onBeforeSave(storage: FormStorage, entity: TEmploymentEntity): IEntity {

    // When Benefit has default, we use type in the default navigation and original value is nullified
    entity.SalaryComponents?.forEach(sc => {
        if (!isObjectEmpty(sc.Default)) {
            delete sc[PrSalaryComponentEntity.Type];
            delete sc[PrSalaryComponentEntity.TypeCode];
            delete sc[PrSalaryComponentEntity.RegularSalaryType];
            delete sc[PrSalaryComponentEntity.RegularSalaryTypeCode];
        }
    });

    // LegislativeExtraSalaries cannot be renamed...
    entity.ExtraSalaries?.forEach(es => {
        if (isLegislativeExtraSalaryItem(es)) {
            es.TemporalPropertyBag?.forEach(item => {
                // it's not possible to change these values for legislative extra salaries
                delete item.Name;
                delete item.ComputationTypeCode;
                delete item.ComputationType;
            });
        }
    });

    // When Benefit has default, we use type in the default navigation and original value is nullified
    entity.Benefits?.forEach(benefit => {
        if (!isObjectEmpty(benefit.Default)) {
            delete benefit[PrEmploymentBenefitEntity.Type];
            delete benefit[PrEmploymentBenefitEntity.TypeCode];
        }
    });

    return entity;
}

export async function removeTemplate(storage: FormStorage<IPrEmploymentEntity>): Promise<void> {
    // todo : remove all
    // storage.setBusy(true);
    //
    // const payload = {
    //     TargetDate: formatDateToDateTimeOffsetString(getUtcDate()), // todo: last non-editable date
    //     SynchronizeWithDefaultValues: false // todo: possible to be removed at all
    // };
    //
    // // await storage.oData.getEntitySetWrapper(EntitySetName.PrEmployments)
    // //     .action(OdataActionName.PrEmploymentRemoveTemplate, storage.data.entity.Id, payload);
    //
    // await storage.reload({ preserveInfos: true, withoutBusy: true });
    //
    // storage.setBusy(false);
}

/**
 * Returns function to extract history from template
 */
export function getExtractHistoryFromSettingsFn(propName: string) {
    return async function getHistoryFn(storage: FormStorage, entity: IEntity, data: TTemporal[]): Promise<TTemporal[]> {
        const hasGeneralSettings = ["VacationWeeks", "SickDays"].includes(propName);

        if (!data) {
            data = [];
        }

        // Second, for globally configurable entities, we may expand additional null values from common settings
        if (hasGeneralSettings) {
            const settings = await getEmploymentGeneralSettings(storage.oData, true);
            const historyData = groupAndOptimizeRanges(settings?.TemporalPropertyBag as TTemporal[], [propName], true);

            data = data.reduce((res: TTemporal[], current: TTemporal) =>
                    res.concat(...replaceSyncedValueWithHistoryValues(current, historyData, PrEntityValueSourceCode.Default)), []);
        }

        return data;
    };
}

export function isBeforeLastClosedPayrollPeriod(storage: FormStorage<IPrEmploymentEntity, IEmploymentCustomData>, useOrigEntity = false): boolean {
    const entity = useOrigEntity ? storage.data.origEntity : storage.data.entity;
    const lastClosedPeriod = (storage.getCustomData() as IEmploymentCustomData).lastClosedPayrollPeriod;
    if (isObjectEmpty(lastClosedPeriod) || !entity.DateCalculatePayslipStart) {
        return false;
    }
    const lastPeriodMonthDate = createDateFromPeriod(lastClosedPeriod);
    return getUtcDayjs(entity.DateCalculatePayslipStart).isSameOrBefore(lastPeriodMonthDate, "month");
}

export async function fetchLastClosedAttendance(storage: FormStorage<IPrEmploymentEntity, IEmploymentCustomData>): Promise<IPrAttendanceEntity> {
    try {
        const rootBc = storage.data.bindingContext;
        if (rootBc.isNew()) {
            return null;
        }
        const res = await storage.oData.getEntitySetWrapper(EntitySetName.PrAttendances).query()
            .filter(`${PrAttendanceEntity.Employment}/Id eq ${rootBc.getKey()} AND ${PrAttendanceEntity.StatusCode} in ('${PrAttendanceStatusCode.Locked}', '${PrAttendanceStatusCode.Processed}')`)
            .select(PrAttendanceEntity.Id, PrAttendanceEntity.Month, PrAttendanceEntity.Year)
            .orderBy(PrAttendanceEntity.Year, false)
            .orderBy(PrAttendanceEntity.Month, false)
            .top(1)
            .fetchData<IPrAttendanceEntity[]>();
        return res.value?.[0] as IPrAttendanceEntity;
    } catch (e) {
        console.error("Error fetching last closed attendance:", e);
        return null;
    }
}
